/**
 * Life4Earth V2 — KPI Configs API Adapter
 *
 * Replaces: localStorage key l4e_project_kpi_config_overrides_v1
 *           from Data/ProjectConfig/projectKpiConfigOverrides.ts
 * Endpoint: /api/KpiConfigs
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";
import { invalidateAndRefetch, QUERY_KEYS } from "./queryCache";

// ── Types ────────────────────────────────────────────────────

export interface KpiConfigResponse {
  id: string;
  // DEV-1075: projectId nullable to support Mini-Project ownership
  // (polymorphic via miniProjectGuid). Exactly one is populated per row.
  projectId: string | null;
  miniProjectGuid?: string | null;
  instanceGuid: string;
  catalogKpiGuid: string;
  category: string;
  displayName: string;
  weight: number;
  // Schedule / SPI
  scheduleTemplateType: string | null;
  varianceYellowPp: number | null;
  varianceRedPp: number | null;
  spiWarningThreshold: number | null;
  spiCriticalThreshold: number | null;
  spiMinBaselinePct: number | null;
  spiWeightPct: number | null;
  // Finance / CPI
  budgetTemplateType: string | null;
  varianceCostYellowPct: number | null;
  varianceCostRedPct: number | null;
  cpiWarningThreshold: number | null;
  cpiCriticalThreshold: number | null;
  cpiMinBaselinePct: number | null;
  cpiWeightPct: number | null;

  // Mission Outcome fields (category = "mission")
  outcomeId: string | null;
  valueType: string | null;
  trackingMode: string | null;
  uom: string | null;
  targetGoal: number | null;
  targetBaseline: number | null;
  cadence: string | null;
  milestoneTotal: number | null;
  // Mission plug-in type (DEV-814 / migration 119).
  // missionTypeConfig is a JSON string at the API boundary; the wizard
  // serializes/deserializes it. Stored server-side as JSONB.
  missionTypeKey: string | null;
  missionTypeConfig: string | null;
  // Role assignment + reporting cadence guard (DEV-814 / migration 121)
  assignedRoleGuid: string | null;
  permissibleDaysLate: number | null;
  // Color mode (DEV-814 / migration 122)
  colorMode: string | null;
  // Pace mode tolerances (DEV-815 / migration 123)
  paceYellowUnitsBehind: number | null;
  paceRedUnitsBehind: number | null;
  // Deadline / tolerance (all value types)
  deadlineDate: string | null;
  warningDays: number | null;
  criticalDays: number | null;
  // Lifecycle dates
  trackingStartDate: string | null;

  displayMode: string;
  isMandatory: boolean;
  isActive: boolean;
  /**
   * True when this row is platform-managed (HSE Incident Tracker rows).
   * Frontend rules: hide from Project Settings KPI configuration tab,
   * never reap in syncProjectKpiConfigsToApi, never PATCH/DELETE through
   * the standard sub-KPI wizard. Configurable only via the dedicated HSE
   * card pencil on the Mission tab. See migration 039 and DEV-822 lock.
   */
  isSystemManaged: boolean;
  direction: string | null;
  greenThreshold: number | null;
  yellowThreshold: number | null;
  mainChart: string | null;
  historyChart: string | null;
  dataBindingNote: string | null;
  // Baseline lock (audit trail)
  baselineLocked: boolean;
  baselineLockedAt: string | null;
  // ── DEV-1075 unified Mission KPI fields (migration 177) ──
  // descriptionRich: TipTap JSON serialized as a string at the wire layer
  //                  (server stores JSONB; parsed on demand by callers).
  // descriptionPlain: derived plain-text projection for snippet rendering
  //                   and future semantic search. Populated by the
  //                   controller write hook.
  // descriptionPhotos: JSON-serialized film-strip array.
  descriptionRich: string | null;
  descriptionPlain: string | null;
  descriptionPhotos: string | null;
  // includedInRollup=false → render the row's own indicator but exclude
  // from the Mission rollup's weighted-average score.
  includedInRollup: boolean;
  // Explicit display order. MP had it; FP relied on creation order.
  position: number;
  // Checklist definition (definition only; per-item completion state
  // lives in mission_metric_points with item_id set).
  checklistItems: string | null;
  // Path A scoring mode. 'numeric' (FP default) | 'qualitative' (MP).
  scoringMode: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKpiConfigRequest {
  // DEV-1075: at least one of projectId or miniProjectGuid must be set
  // (XOR enforced by DB CHECK constraint chk_kpi_config_owner_xor).
  projectId?: string;
  miniProjectGuid?: string;
  instanceGuid: string;
  catalogKpiGuid: string;
  category: string;
  displayName: string;
  weight?: number;
  // Schedule / SPI
  scheduleTemplateType?: string;
  varianceYellowPp?: number;
  varianceRedPp?: number;
  spiWarningThreshold?: number;
  spiCriticalThreshold?: number;
  spiMinBaselinePct?: number;
  spiWeightPct?: number;
  // Finance / CPI
  budgetTemplateType?: string;
  varianceCostYellowPct?: number;
  varianceCostRedPct?: number;
  cpiWarningThreshold?: number;
  cpiCriticalThreshold?: number;
  cpiMinBaselinePct?: number;
  cpiWeightPct?: number;

  // Mission Outcome fields
  outcomeId?: string;
  valueType?: string;
  trackingMode?: string;
  uom?: string;
  targetGoal?: number;
  targetBaseline?: number;
  cadence?: string;
  milestoneTotal?: number;
  // Mission plug-in type (DEV-814 / migration 119)
  missionTypeKey?: string;
  /** JSON-serialized typeConfig. The wizard owns the schema per type. */
  missionTypeConfig?: string;
  // Role assignment + reporting cadence guard (DEV-814 / migration 121)
  assignedRoleGuid?: string;
  permissibleDaysLate?: number;
  // Color mode (DEV-814 / migration 122)
  colorMode?: string;
  // Pace mode tolerances (DEV-815 / migration 123)
  paceYellowUnitsBehind?: number;
  paceRedUnitsBehind?: number;
  // Deadline / tolerance (all value types)
  deadlineDate?: string;
  warningDays?: number;
  criticalDays?: number;
  // Lifecycle dates
  trackingStartDate?: string;

  displayMode?: string;
  isMandatory?: boolean;
  direction?: string;
  greenThreshold?: number;
  yellowThreshold?: number;
  mainChart?: string;
  historyChart?: string;
  dataBindingNote?: string;
  // Baseline lock (audit trail)
  baselineLocked?: boolean;
  baselineLockedAt?: string;
  // ── DEV-1075 unified Mission KPI fields ──
  descriptionRich?: string;
  descriptionPlain?: string;
  descriptionPhotos?: string;
  includedInRollup?: boolean;
  position?: number;
  checklistItems?: string;
  scoringMode?: string;
}

export interface UpdateKpiConfigRequest {
  displayName?: string;
  weight?: number;
  // Schedule / SPI
  scheduleTemplateType?: string;
  varianceYellowPp?: number;
  varianceRedPp?: number;
  spiWarningThreshold?: number;
  spiCriticalThreshold?: number;
  spiMinBaselinePct?: number;
  spiWeightPct?: number;
  // Finance / CPI
  budgetTemplateType?: string;
  varianceCostYellowPct?: number;
  varianceCostRedPct?: number;
  cpiWarningThreshold?: number;
  cpiCriticalThreshold?: number;
  cpiMinBaselinePct?: number;
  cpiWeightPct?: number;

  // Mission Outcome fields
  outcomeId?: string;
  valueType?: string;
  trackingMode?: string;
  uom?: string;
  targetGoal?: number;
  targetBaseline?: number;
  cadence?: string;
  milestoneTotal?: number;
  // Mission plug-in type (DEV-814 / migration 119)
  missionTypeKey?: string;
  /** JSON-serialized typeConfig. The wizard owns the schema per type. */
  missionTypeConfig?: string;
  // Role assignment + reporting cadence guard (DEV-814 / migration 121)
  assignedRoleGuid?: string;
  permissibleDaysLate?: number;
  // Color mode (DEV-814 / migration 122)
  colorMode?: string;
  // Pace mode tolerances (DEV-815 / migration 123)
  paceYellowUnitsBehind?: number;
  paceRedUnitsBehind?: number;
  // Deadline / tolerance (all value types)
  deadlineDate?: string;
  warningDays?: number;
  criticalDays?: number;
  // Lifecycle dates
  trackingStartDate?: string;

  displayMode?: string;
  isMandatory?: boolean;
  isActive?: boolean;
  direction?: string;
  greenThreshold?: number;
  yellowThreshold?: number;
  mainChart?: string;
  historyChart?: string;
  dataBindingNote?: string;
  // Baseline lock (audit trail)
  baselineLocked?: boolean;
  baselineLockedAt?: string;
  // ── DEV-1075 unified Mission KPI fields ──
  descriptionRich?: string;
  descriptionPlain?: string;
  descriptionPhotos?: string;
  includedInRollup?: boolean;
  position?: number;
  checklistItems?: string;
  scoringMode?: string;
}

// ── Helpers: mission_type_config (JSON string ↔ object) ─────
//
// The API stores mission_type_config as JSONB but treats it as opaque text
// across the wire. We translate at the API boundary so wizard code can work
// with a plain object. The wizard schema lives in src/kpi-engine/missionTypes/.
//
// Both helpers are forgiving: a parse failure or an empty value resolves to
// `undefined`, which lets MissionKpiWizard.createInitialState() fall through
// to its reverse-bridge and reconstruct config from the legacy fields.

function parseMissionTypeConfig(raw: string | null | undefined): Record<string, unknown> | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;
  // Server may return either a JSON-encoded string or, depending on
  // serializer settings, the parsed object already. Handle both.
  if (typeof raw === "object") return raw as Record<string, unknown>;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    // Bad JSON in storage — log once and let reverse-bridge handle it.
    if (typeof console !== "undefined") {
      console.warn("[kpiConfigs] mission_type_config failed to parse; falling back to reverse-bridge", raw);
    }
    return undefined;
  }
}

function serializeMissionTypeConfig(obj: unknown): string | null {
  if (obj === null || obj === undefined) return null;
  if (typeof obj === "string") return obj; // already serialized — pass through
  try {
    return JSON.stringify(obj);
  } catch {
    return null;
  }
}

// ── Shape Converters ────────────────────────────────────────

/**
 * Convert a KpiConfigResponse to the SubKpiInstance shape
 * that scheduleDataAdapter and instanceScoring expect.
 * This bridges the API response → the existing kpi-engine types.
 */
export function kpiConfigToSubKpiInstance(
  k: KpiConfigResponse
): Record<string, any> {
  return {
    instance_guid: normalizeGuid(k.instanceGuid),
    // catalog_kpi_guid is a semantic string identifier (e.g. "kpi_sched_progress_v1"),
    // NOT a UUID. Do NOT normalizeGuid() it — that would uppercase it and break
    // case-sensitive catalog lookups in isProgressKpiCatalog(), the KpiNavigation
    // component renderer map, and everywhere else that matches on lowercase catalog IDs.
    catalog_kpi_guid: k.catalogKpiGuid ?? "",
    display_name: k.displayName,
    display_mode: k.displayMode || "full",
    // Schedule / SPI
    variance_yellow_pp: k.varianceYellowPp ?? undefined,
    variance_red_pp: k.varianceRedPp ?? undefined,
    schedule_template_type: k.scheduleTemplateType ?? undefined,
    spi_warning_threshold: k.spiWarningThreshold ?? undefined,
    spi_critical_threshold: k.spiCriticalThreshold ?? undefined,
    spi_min_baseline_pct: k.spiMinBaselinePct ?? undefined,
    spi_weight_pct: k.spiWeightPct ?? undefined,
    // Finance / CPI
    budget_template_type: k.budgetTemplateType ?? undefined,
    variance_cost_yellow_pct: k.varianceCostYellowPct ?? undefined,
    variance_cost_red_pct: k.varianceCostRedPct ?? undefined,
    cpi_warning_threshold: k.cpiWarningThreshold ?? undefined,
    cpi_critical_threshold: k.cpiCriticalThreshold ?? undefined,
    cpi_min_baseline_pct: k.cpiMinBaselinePct ?? undefined,
    cpi_weight_pct: k.cpiWeightPct ?? undefined,

    // Mission Outcome fields
    // outcome_id is a semantic string identifier (e.g. "OUT-TREES-PLANTED"),
    // NOT a UUID. Preserve its original casing.
    outcome_id: k.outcomeId ?? undefined,
    value_type: k.valueType ?? undefined,
    tracking_mode: k.trackingMode ?? undefined,
    uom: k.uom ?? undefined,
    target_goal: k.targetGoal ?? undefined,
    target_baseline: k.targetBaseline ?? undefined,
    cadence: k.cadence ?? undefined,
    milestone_total: k.milestoneTotal ?? undefined,
    // Mission plug-in type (DEV-814 / migration 119).
    // The server returns missionTypeConfig as a JSON string (JSONB column);
    // parse it here so wizard code sees a typed object. If parse fails (legacy
    // garbage, half-written rows), fall back to undefined and let the wizard's
    // reverseBridgeFromLegacy reconstruct from legacy fields.
    mission_type_key: k.missionTypeKey ?? undefined,
    mission_type_config: parseMissionTypeConfig(k.missionTypeConfig),
    // Role assignment + reporting cadence guard (DEV-814 / migration 121)
    assigned_role_guid: k.assignedRoleGuid ?? undefined,
    permissible_days_late: k.permissibleDaysLate ?? undefined,
    // Color mode (DEV-814 / migration 122)
    color_mode: k.colorMode ?? undefined,
    // Pace mode tolerances (DEV-815 / migration 123)
    pace_yellow_units_behind: k.paceYellowUnitsBehind ?? undefined,
    pace_red_units_behind: k.paceRedUnitsBehind ?? undefined,
    // Deadline / tolerance (all value types)
    deadline_date: k.deadlineDate ?? undefined,
    warning_days: k.warningDays ?? undefined,
    critical_days: k.criticalDays ?? undefined,
    // Lifecycle dates
    tracking_start_date: k.trackingStartDate ?? undefined,

    weight: k.weight,
    is_mandatory: k.isMandatory,
    // Preserve the platform-managed flag through the seed-shape conversion.
    // Without this, local SubKpiInstance has is_system_managed=undefined for
    // HSE rows; syncProjectKpiConfigsToApi's defensive skip-on-write check
    // (`if (inst.is_system_managed === true) continue;`) never fires and the
    // bulk sync tries to POST a new row with the existing instance_guid →
    // server rejects with 500. See diagnosis 2026-05-11.
    is_system_managed: !!k.isSystemManaged,
    // Baseline lock (audit trail)
    baseline_locked: !!k.baselineLocked,
    baseline_locked_at: k.baselineLockedAt ?? undefined,
    direction: k.direction ?? undefined,
    green_threshold: k.greenThreshold ?? undefined,
    yellow_threshold: k.yellowThreshold ?? undefined,
    main_chart: k.mainChart ?? undefined,
    history_chart: k.historyChart ?? undefined,
    data_binding_note: k.dataBindingNote ?? undefined,
    // ── DEV-1075 unified fields ──
    // Owner discriminator. project_id stays the SubKpiInstance.project_id
    // (numeric path); mini_project_guid is exposed for downstream
    // consumers that route MP-owned rows into the new unified surfaces.
    mini_project_guid: k.miniProjectGuid ?? undefined,
    description_rich: k.descriptionRich ?? undefined,
    description_plain: k.descriptionPlain ?? undefined,
    description_photos: k.descriptionPhotos ?? undefined,
    // included_in_rollup defaults true server-side; surface as boolean
    // (not "?? undefined") so the engine sees an explicit false when
    // the user opts the row out of rollup contribution.
    included_in_rollup: k.includedInRollup,
    position: k.position ?? 0,
    checklist_items: k.checklistItems ?? undefined,
    // scoring_mode is NOT NULL DEFAULT 'numeric' server-side. Pass it
    // through verbatim — engine branches on === 'qualitative' (Path A).
    scoring_mode: k.scoringMode ?? 'numeric',
  };
}

/**
 * Group KPI configs by project ID, converting each to SubKpiInstance shape.
 * Returns a Map<projectGuid, { schedule: SubKpiInstance[], ... }>.
 */
export interface GroupedKpiConfigs {
  schedule: Record<string, any>[];
  finance: Record<string, any>[];
  mission: Record<string, any>[];
}

export function groupKpiConfigsByProject(
  configs: KpiConfigResponse[]
): Map<string, GroupedKpiConfigs> {
  const map = new Map<string, GroupedKpiConfigs>();
  for (const c of configs) {
    const pg = normalizeGuid(c.projectId);
    if (!pg) continue;
    if (!map.has(pg)) {
      map.set(pg, { schedule: [], finance: [], mission: [] });
    }
    const entry = map.get(pg)!;
    if (c.category === "schedule") {
      entry.schedule.push(kpiConfigToSubKpiInstance(c));
    } else if (c.category === "finance") {
      entry.finance.push(kpiConfigToSubKpiInstance(c));
    } else if (c.category === "mission") {
      entry.mission.push(kpiConfigToSubKpiInstance(c));
    }
  }
  return map;
}

// ── API Functions ────────────────────────────────────────────

/**
 * Fetch ALL active KPI configs across all projects.
 * Used by App.tsx for bulk pre-fetch.
 */
export async function fetchAllKpiConfigs(
  activeOnly: boolean = true
): Promise<KpiConfigResponse[]> {
  return apiFetch<KpiConfigResponse[]>(
    `/api/KpiConfigs/all?activeOnly=${activeOnly}`
  );
}

export async function fetchKpiConfigsByProject(
  projectId: string,
  activeOnly: boolean = true
): Promise<KpiConfigResponse[]> {
  return apiFetch<KpiConfigResponse[]>(
    `/api/KpiConfigs?projectId=${projectId}&activeOnly=${activeOnly}`
  );
}

/**
 * DEV-1075 Phase 7.1 — fetch unified KpiConfig rows for a single
 * Mini-Project. Parallel to fetchKpiConfigsByProject; backed by the
 * new GET /api/KpiConfigs/by-mini-project endpoint.
 *
 * Returned rows have category='mission' + scoring_mode='qualitative'
 * + a populated miniProjectGuid (projectId is null). Phase 7.1's
 * miniProjectGoalView derivation consumes this alongside the matching
 * mission_metric_points to produce the goal-card-friendly view.
 */
export async function fetchKpiConfigsByMiniProject(
  miniProjectGuid: string,
  activeOnly: boolean = true
): Promise<KpiConfigResponse[]> {
  return apiFetch<KpiConfigResponse[]>(
    `/api/KpiConfigs/by-mini-project?miniProjectGuid=${miniProjectGuid}&activeOnly=${activeOnly}`
  );
}

export async function fetchKpiConfigById(
  id: string
): Promise<KpiConfigResponse> {
  return apiFetch<KpiConfigResponse>(`/api/KpiConfigs/${id}`);
}

export async function createKpiConfig(
  request: CreateKpiConfigRequest
): Promise<KpiConfigResponse> {
  return apiFetch<KpiConfigResponse>("/api/KpiConfigs", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function updateKpiConfig(
  id: string,
  request: UpdateKpiConfigRequest
): Promise<KpiConfigResponse> {
  return apiFetch<KpiConfigResponse>(`/api/KpiConfigs/${id}`, {
    method: "PUT",
    body: JSON.stringify(request),
  });
}

export async function deactivateKpiConfig(id: string): Promise<void> {
  return apiFetch<void>(`/api/KpiConfigs/${id}`, {
    method: "DELETE",
  });
}

// ── Batch Sync Helper ────────────────────────────────────────

/**
 * Sync a project's KPI instances to the API.
 * Strategy:
 *   1. Fetch existing API configs for the project.
 *   2. For each local instance → create or update in the API.
 *   3. For any API instance not in the local set → deactivate.
 *
 * Fire-and-forget from the caller; failures are logged but do not block the UI.
 */
export async function syncProjectKpiConfigsToApi(
  projectId: string,
  categoryInstances: Partial<Record<string, any[]>>,
): Promise<void> {
  const existing = await fetchKpiConfigsByProject(projectId, false);
  // Migration 039 / DEV-822: HSE rows (isSystemManaged=true) need
  // differentiated handling across the three sync operations:
  //
  //   CREATE  — never create a system-managed row from this sync. The
  //             provisioning path lives in ProjectsController and
  //             migrations 035/040; the sub-KPI wizard isn't allowed
  //             to spin up new HSE rows.
  //   UPDATE  — system-managed rows CAN receive display_name + mission_
  //             type_config changes (the HSE card pencil legitimately
  //             edits both). The server's guard rejects structural-field
  //             attempts; we send a whitelisted payload below to avoid
  //             tripping it.
  //   REAP    — never deactivate a system-managed row. They're permanent
  //             per the DEV-822 lock. The systemManagedGuids set carries
  //             that filter through to the reap loop.
  //
  // Bug history: previously the lookup map excluded system-managed rows
  // entirely. The update branch then couldn't find them, fell to create,
  // and POSTed a duplicate instance_guid → 500. Display-name edits on HSE
  // rows silently failed as a result. Fixed 2026-05-11.
  const existingByInstanceGuid = new Map<string, KpiConfigResponse>();
  const systemManagedGuids = new Set<string>();
  for (const e of existing) {
    const ig = String(e.instanceGuid).toUpperCase();
    existingByInstanceGuid.set(ig, e);
    if (e.isSystemManaged) systemManagedGuids.add(ig);
  }

  const seenGuids = new Set<string>();

  for (const [category, instances] of Object.entries(categoryInstances || {})) {
    if (!Array.isArray(instances)) continue;
    for (const inst of instances) {
      const ig = String(inst.instance_guid || "").trim().toUpperCase();
      if (!ig) continue;
      // System-managed rows ARE allowed through this loop now — the
      // update branch below sends a whitelisted payload that the server
      // accepts on locked rows (display_name + mission_type_config). The
      // create branch skips them separately. seenGuids tracks them too so
      // the reap loop's "not seen locally" rule doesn't misfire (though
      // systemManagedGuids would protect them anyway).
      seenGuids.add(ig);

      const apiRow = existingByInstanceGuid.get(ig);
      const payload: CreateKpiConfigRequest = {
        projectId,
        instanceGuid: String(inst.instance_guid),
        catalogKpiGuid: String(inst.catalog_kpi_guid || ""),
        category,
        displayName: String(inst.display_name || ""),
        weight: typeof inst.weight === "number" ? inst.weight : 0,
        // Schedule / SPI
        scheduleTemplateType: inst.schedule_template_type || null,
        varianceYellowPp: inst.variance_yellow_pp ?? null,
        varianceRedPp: inst.variance_red_pp ?? null,
        spiWarningThreshold: inst.spi_warning_threshold ?? null,
        spiCriticalThreshold: inst.spi_critical_threshold ?? null,
        spiMinBaselinePct: inst.spi_min_baseline_pct ?? null,
        spiWeightPct: inst.spi_weight_pct ?? null,
        // Finance / CPI
        budgetTemplateType: inst.budget_template_type || null,
        varianceCostYellowPct: inst.variance_cost_yellow_pct ?? null,
        varianceCostRedPct: inst.variance_cost_red_pct ?? null,
        cpiWarningThreshold: inst.cpi_warning_threshold ?? null,
        cpiCriticalThreshold: inst.cpi_critical_threshold ?? null,
        cpiMinBaselinePct: inst.cpi_min_baseline_pct ?? null,
        cpiWeightPct: inst.cpi_weight_pct ?? null,

        // Mission Outcome fields
        outcomeId: inst.outcome_id || null,
        valueType: inst.value_type || null,
        trackingMode: inst.tracking_mode || null,
        uom: inst.uom || null,
        targetGoal: inst.target_goal ?? null,
        targetBaseline: inst.target_baseline ?? null,
        cadence: inst.cadence || null,
        milestoneTotal: inst.milestone_total ?? null,
        // Mission plug-in type (DEV-814 / migration 119).
        // mission_type_config arrives as an object from the wizard; the
        // API expects a JSON string so it can store as JSONB. We serialize
        // here at the boundary so wizard code stays object-shaped.
        missionTypeKey: inst.mission_type_key || null,
        missionTypeConfig: serializeMissionTypeConfig(inst.mission_type_config),
        // Role assignment + reporting cadence guard (DEV-814 / migration 121)
        assignedRoleGuid: inst.assigned_role_guid || null,
        permissibleDaysLate: inst.permissible_days_late ?? null,
        // Color mode (DEV-814 / migration 122)
        colorMode: inst.color_mode || null,
        // Pace mode tolerances (DEV-815 / migration 123)
        paceYellowUnitsBehind: inst.pace_yellow_units_behind ?? null,
        paceRedUnitsBehind: inst.pace_red_units_behind ?? null,
        // Deadline / tolerance (all value types)
        deadlineDate: inst.deadline_date || null,
        warningDays: inst.warning_days ?? null,
        criticalDays: inst.critical_days ?? null,
        // Lifecycle dates
        trackingStartDate: inst.tracking_start_date || null,

        displayMode: inst.display_mode || "full",
        isMandatory: !!inst.is_mandatory,
        // Baseline lock (audit trail)
        baselineLocked: !!inst.baseline_locked,
        baselineLockedAt: inst.baseline_locked_at || null,
        direction: inst.direction || null,
        greenThreshold: inst.green_threshold ?? null,
        yellowThreshold: inst.yellow_threshold ?? null,
        mainChart: inst.main_chart || null,
        historyChart: inst.history_chart || null,
        dataBindingNote: inst.data_binding_note || null,
      };

      if (apiRow) {
        // UPDATE path. For system-managed rows (HSE), send ONLY the fields
        // the server allows on those rows — display_name and mission_type_
        // config. Sending the full payload risks tripping the structural-
        // change guard on values that drifted slightly between client and
        // server (e.g., a numeric field re-cast). Whitelisting here keeps
        // legitimate HSE edits (pencil → display name + period/mode in
        // mission_type_config) flowing while everything else stays locked.
        const isSysMgd = systemManagedGuids.has(ig);
        const updatePayload = isSysMgd
          ? {
              displayName: payload.displayName,
              missionTypeConfig: payload.missionTypeConfig,
            }
          : payload;
        await updateKpiConfig(apiRow.id, updatePayload as any);
      } else {
        // CREATE path. Skip system-managed rows — they're provisioned
        // server-side; this sync isn't allowed to create new HSE rows.
        if ((inst as any)?.is_system_managed === true) continue;
        await createKpiConfig(payload);
      }
    }
  }

  // Deactivate API configs that no longer exist locally.
  //
  // REAP rules:
  //   1. Never reap system-managed rows (HSE). The DEV-822 lock makes
  //      them permanent; the systemManagedGuids set carries that filter.
  //   2. Errors are logged loudly. The previous `.catch(() => {})` swallowed
  //      503/403/network failures, so when a legitimate row got rejected
  //      by the server-side guard it failed silently. Loud failure beats
  //      silent data loss — surfacing the error in the console is the
  //      minimum bar. Future improvement: surface a toast.
  existingByInstanceGuid.forEach((apiRow, guid) => {
    if (systemManagedGuids.has(guid)) return;
    if (!seenGuids.has(guid) && apiRow.isActive) {
      deactivateKpiConfig(apiRow.id).catch((err) => {
        console.error(
          `[syncProjectKpiConfigsToApi] Failed to deactivate KpiConfig ${apiRow.id} ` +
            `(project=${projectId}, instance_guid=${apiRow.instanceGuid}, ` +
            `display_name=${apiRow.displayName})`,
          err,
        );
      });
    }
  });

  // Invalidate the KPI configs cache AND trigger a forced refetch so
  // useApiKpiConfigs re-fetches from the API immediately. Without the
  // invalidate() call, the cache still considers the old data "fresh"
  // (< 60s) and returns stale results — which hydrateKpiConfigs() then
  // uses to overwrite the in-memory store, wiping the newly added subKPI.
  invalidateAndRefetch(QUERY_KEYS.kpiConfigs);
}
